import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getTaxDetails, clearTaxDetails, updateTax, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';
import { getGuardedInputValue } from '../../../../shared/utils.js/fieldValidation';

const EditTax = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { taxDetails, isLoading, error, success } = useSelector((s) => s.auth);
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  useEffect(() => {
    dispatch(getTaxDetails(id));
    return () => {
      dispatch(clearTaxDetails());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (taxDetails) {
      setForm({
        name_ar: taxDetails.name_ar || '',
        name_en: taxDetails.name_en || '',
        rate: taxDetails.rate != null ? taxDetails.rate : '',
        is_active: Boolean(taxDetails.is_active),
      });
    }
  }, [taxDetails]);

  const handleChange = (e) => {
    const { name, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      const filtered = getGuardedInputValue(e.target);
      setForm((prev) => ({ ...prev, [name]: filtered }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name_ar || !form.name_en || form.rate === '') {
      toast.error(t('taxes.form.requiredFields'), { rtl: isRTL });
      return;
    }

    try {
      setIsSubmitting(true);
      // Create FormData for offline support
      const formData = new FormData();
      formData.append('name_ar', form.name_ar);
      formData.append('name_en', form.name_en);
      formData.append('rate', Number(form.rate));
      formData.append('is_active', Boolean(form.is_active));

      try {
        // Try to use offline request manager first
        const result = await makeOfflineRequest(`/taxes/${id}`, 'PUT', formData);
        toast.success(result?.message || t('taxes.form.updateSuccess', { defaultValue: 'تم تعديل الضريبة بنجاح' }), { rtl: isRTL });
        dispatch(clearSuccess());
        navigate('/taxes');
      } catch (offlineError) {
        // If request was saved offline, show appropriate message
        if (offlineError.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(
            isRTL
              ? 'تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت'
              : 'Request saved and will be sent automatically when connection is restored',
            { rtl: isRTL, autoClose: 5000 }
          );
          dispatch(clearSuccess());
          // Still navigate away since the request is saved
          navigate('/taxes');
          return;
        }

        // If it's a different error, try the original Redux dispatch as fallback
        const payload = {
          name_ar: form.name_ar,
          name_en: form.name_en,
          rate: Number(form.rate),
          is_active: Boolean(form.is_active),
        };
        const res = await dispatch(updateTax({ id, data: payload })).unwrap();
        toast.success(res?.message || t('taxes.form.updateSuccess', { defaultValue: 'تم تعديل الضريبة بنجاح' }), { rtl: isRTL });
        dispatch(clearSuccess());
        navigate('/taxes');
      }
    } catch (err) {
      let errorMessage = t('taxes.form.updateError', { defaultValue: 'فشل تعديل الضريبة' });

      // Handle different error formats from Redux Toolkit unwrap()
      if (typeof err === 'string') {
        errorMessage = err;
      } else {
        const errorData = err?.response?.data || err;
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.errors) {
          const errorMessages = Object.values(errorData.errors).flat();
          errorMessage = errorMessages.join(', ');
        } else if (err?.message) {
          errorMessage = err.message;
        }
      }

      // Display toast immediately
      toast.error(errorMessage, {
        rtl: isRTL,
        autoClose: 5000,
      });
      dispatch(clearError());
    } finally {
      setIsSubmitting(false);
    }
  };

  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px', textAlign }}>{t('taxes.form.editTitle')}</h1>
      <form
        onSubmit={handleSubmit}
        style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 24, display: 'grid', gap: 16 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ ...labelStyle, textAlign }}>{t('taxes.form.nameAr')} <span style={{ color: '#dc3545' }}>*</span></label>
            <input
              type="text"
              name="name_ar"
              value={form.name_ar}
              onChange={handleChange}
              style={{ ...inputStyle, direction: 'rtl', textAlign: 'right' }}
            />
          </div>
          <div>
            <label style={{ ...labelStyle, textAlign }}>{t('taxes.form.nameEn')} <span style={{ color: '#dc3545' }}>*</span></label>
            <input
              type="text"
              name="name_en"
              value={form.name_en}
              onChange={handleChange}
              style={{ ...inputStyle, direction: 'ltr', textAlign: 'left' }}
            />
          </div>
          <div>
            <label style={{ ...labelStyle, textAlign }}>{t('taxes.form.rate')} <span style={{ color: '#dc3545' }}>*</span></label>
            <input
              type="number"
              name="rate"
              
              value={form.rate}
              onChange={handleChange}
              style={{ ...inputStyle, direction: dir, textAlign }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 30, direction: dir }}>
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={form.is_active}
              onChange={handleChange}
            />
            <label htmlFor="is_active" style={{ textAlign }}>{t('taxes.form.isActive')}</label>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate('/taxes')}
            style={{ background: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', direction: dir, textAlign }}
          >
            {t('taxes.form.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading || isSubmitting}
            style={{ background: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', direction: dir, textAlign }}
          >
            {isSubmitting ? t('taxes.form.saving', { defaultValue: 'جاري الحفظ...' }) : t('taxes.form.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

const labelStyle = { display: 'block', marginBottom: 6, fontSize: 14 };
const inputStyle = {
  width: '100%',
  padding: 12,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  fontSize: 14,
};

export default EditTax;


